import { useState } from 'react'
import { router } from '@inertiajs/react'
import { ResultsOwnershipChartPlacement, roleOptionsForAssignment, UserRole } from '../../types'
import ConfirmModal from '../shared/ConfirmModal'
import ChartPlacementForm from './ChartPlacementForm'
import { LABELS } from '../../pages/AccountabilityCharts/labels'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  teams: { id: number; name: string }[]
  managers: { id: number; name: string }[]
  /** Roles the current viewer is allowed to hand out (server-enforced too). */
  assignableRoles: string[]
  /**
   * The org's active chart, when the viewer may place someone on it. Absent (or
   * not placeable) means the post-creation prompt is skipped silently.
   */
  chart?: ResultsOwnershipChartPlacement | null
  onClose: () => void
}

type Mode = 'invite' | 'create'

export default function InviteModal({ teams, managers, assignableRoles, chart, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('invite')
  // Set once a direct create succeeds and the chart prompt applies: the modal
  // then stays open on the prompt instead of closing, carrying the new person.
  const [createdPerson, setCreatedPerson] = useState<{ id: number; full_name: string } | null>(null)
  const [placing, setPlacing] = useState(false)

  const [emails, setEmails] = useState('')
  const [role, setRole] = useState<UserRole>('member')
  const [selectedTeams, setSelectedTeams] = useState<number[]>([])
  const [managerId, setManagerId] = useState<string>('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [confirmExisting, setConfirmExisting] = useState<string | null>(null)

  const roleOptions = roleOptionsForAssignment(assignableRoles)

  const postInvite = (confirm: boolean) => {
    setSubmitting(true)
    router.post('/invitations', {
      emails,
      role,
      team_ids: selectedTeams,
      manager_id: managerId || null,
      message: message || null,
      confirm_existing: confirm ? '1' : null,
    }, {
      onFinish: () => setSubmitting(false),
      onSuccess: () => onClose(),
      onError: (errs) => {
        if (errs.confirm_existing) setConfirmExisting(errs.confirm_existing as string)
      },
    })
  }

  const postCreate = (confirm: boolean) => {
    setSubmitting(true)
    router.post('/people', {
      user: {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        role,
        manager_id: managerId || null,
        team_ids: selectedTeams,
        confirm_existing: confirm ? '1' : null,
      }
    }, {
      // Keep this component mounted so the chart prompt can take over the modal
      // once the person exists; the People list props still refresh underneath.
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
      onSuccess: (page) => {
        const flash = (page.props as { flash?: { created_user_id?: number | null } }).flash
        const createdId = flash?.created_user_id
        // The prompt only appears when it can actually be acted on: a chart the
        // viewer may add to, and a person the server confirmed it created.
        if (chart?.can_place && createdId) {
          setCreatedPerson({ id: createdId, full_name: `${firstName.trim()} ${lastName.trim()}`.trim() })
        } else {
          onClose()
        }
      },
      onError: (errs) => {
        if (errs.confirm_existing) setConfirmExisting(errs.confirm_existing as string)
      },
    })
  }

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
    let out = ''
    const arr = new Uint32Array(14)
    crypto.getRandomValues(arr)
    arr.forEach(n => { out += chars[n % chars.length] })
    return out
  }

  const handleSubmitInvite = (e: React.FormEvent) => {
    e.preventDefault()
    postInvite(false)
  }

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault()
    postCreate(false)
  }

  const toggleTeam = (teamId: number) => {
    setSelectedTeams(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-elevated w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-stone/20 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary font-heading">
            {createdPerson ? `Add to ${LABELS.feature}` : mode === 'invite' ? 'Invite People' : 'Create User'}
          </h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {createdPerson && chart ? (
          <div className="p-6 space-y-5">
            <p className="text-sm text-text-primary">
              {createdPerson.full_name} was created. Would you like to add this user to your {LABELS.feature}?
            </p>
            {placing ? (
              <ChartPlacementForm
                chart={chart}
                person={createdPerson}
                cancelLabel="Back"
                onCancel={() => setPlacing(false)}
                onPlaced={onClose}
              />
            ) : (
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-stone/10 rounded-lg transition-colors"
                >
                  No, skip for now
                </button>
                <button
                  type="button"
                  onClick={() => setPlacing(true)}
                  className="px-5 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors"
                >
                  Yes, add them
                </button>
              </div>
            )}
          </div>
        ) : (
        <>
        <div className="px-6 pt-4">
          <div className="flex rounded-lg border border-stone/30 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setMode('invite')}
              className={`flex-1 px-3 py-2 font-medium transition-colors ${
                mode === 'invite'
                  ? 'bg-[#351E1D] text-white'
                  : 'text-text-muted hover:bg-stone/10'
              }`}
            >
              Send Invitation Email
            </button>
            <button
              type="button"
              onClick={() => setMode('create')}
              className={`flex-1 px-3 py-2 font-medium transition-colors ${
                mode === 'create'
                  ? 'bg-[#351E1D] text-white'
                  : 'text-text-muted hover:bg-stone/10'
              }`}
            >
              Create Directly
            </button>
          </div>
        </div>

        {mode === 'invite' ? (
          <form onSubmit={handleSubmitInvite} className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Email addresses</label>
              <textarea
                value={emails}
                onChange={e => setEmails(e.target.value)}
                placeholder="Enter emails separated by commas"
                rows={3}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal resize-none"
                required
              />
              <p className="text-sm text-text-secondary mt-1">Separate multiple emails with commas</p>
            </div>

            <RolePicker role={role} onChange={setRole} options={roleOptions} />
            <TeamPicker teams={teams} selectedTeams={selectedTeams} toggleTeam={toggleTeam} />
            <ManagerPicker managers={managers} managerId={managerId} onChange={setManagerId} />

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Personal message (optional)</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Add a personal message to the invitation email..."
                rows={2}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal resize-none"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-primary hover:text-text-primary transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !emails.trim()}
                className="px-5 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send Invitations'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmitCreate} className="p-6 space-y-5">
            <p className="text-sm text-text-muted -mt-1">
              Create the user immediately with a password you set. Share the
              login credentials with them directly — no email is sent.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">First name</label>
                <input
                  required
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Last name</label>
                <input
                  required
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                placeholder="user@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Password</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  required
                  minLength={6}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="flex-1 border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary font-mono focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                  placeholder="Min 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setPassword(generatePassword())}
                  className="shrink-0 px-3 py-2 text-sm text-pinnacle-red border border-stone/30 rounded-lg hover:bg-stone/10 transition-colors"
                >
                  Generate
                </button>
              </div>
              <p className="text-xs text-text-muted mt-1">Shown in plain text so you can copy and share it</p>
            </div>

            <RolePicker role={role} onChange={setRole} options={roleOptions} />
            <TeamPicker teams={teams} selectedTeams={selectedTeams} toggleTeam={toggleTeam} />
            <ManagerPicker managers={managers} managerId={managerId} onChange={setManagerId} />

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-primary hover:text-text-primary transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !email.trim() || !firstName.trim() || !lastName.trim() || password.length < 6}
                className="px-5 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        )}
        </>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmExisting !== null}
        title="Add existing Pinnacle Navigator user?"
        message={confirmExisting || ''}
        confirmLabel="Yes, add them"
        variant="warning"
        submitting={submitting}
        onConfirm={() => {
          setConfirmExisting(null)
          mode === 'invite' ? postInvite(true) : postCreate(true)
        }}
        onClose={() => setConfirmExisting(null)}
      />
    </div>
  )
}

function RolePicker({ role, onChange, options }: {
  role: UserRole
  onChange: (v: UserRole) => void
  options: { value: UserRole; label: string }[]
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1.5">Role</label>
      <select
        value={role}
        onChange={e => onChange(e.target.value as UserRole)}
        className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  )
}

function TeamPicker({ teams, selectedTeams, toggleTeam }: {
  teams: { id: number; name: string }[]
  selectedTeams: number[]
  toggleTeam: (id: number) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1.5">Add to teams</label>
      <div className="border border-stone/30 rounded-lg max-h-36 overflow-y-auto p-2 space-y-1">
        {teams.map(team => (
          <label key={team.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone/10 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedTeams.includes(team.id)}
              onChange={() => toggleTeam(team.id)}
              className="w-4 h-4 text-pinnacle-red focus:ring-pinnacle-red/20 border-stone/40 rounded"
            />
            <span className="text-sm text-text-primary">{team.name}</span>
          </label>
        ))}
        {teams.length === 0 && (
          <p className="text-sm text-text-secondary px-2 py-1">No teams available</p>
        )}
      </div>
    </div>
  )
}

function ManagerPicker({ managers, managerId, onChange }: {
  managers: { id: number; name: string }[]
  managerId: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1.5">Manager (optional)</label>
      <select
        value={managerId}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
      >
        <option value="">No manager</option>
        {sortPeople(managers).map(m => (
          <option key={m.id} value={m.id}>{m.name}</option>
        ))}
      </select>
    </div>
  )
}
